import * as React from 'react';
import ITradeHistory, { IFilteredTradesList, TTradeHistory } from './ITradeHistory';
import CurrencyElement from '@appComponents/CurrencyElement/CurrencyElement';
import Pagination from '@appComponents/Pagination/Pagination';
import DateRangePicker from '@market/components/DateRangePicker/DateRangePicker';
import Zoom from '../Zoom/Zoom';
import moment from 'moment';
import TradeHistoryChart from '../TradeHistoryChart/TradeHistoryChart';
import CsvGenerator from '@appComponents/CsvGenerator/CsvGenerator';
import { formatDate } from '@features/utils/functions';

import * as style from './TradeHistoryStyle.module.scss'
import * as elements from '@appStyles/_elements.module.scss';

export default class TradeHistory extends React.Component<ITradeHistory, TTradeHistory> {
  MAX_PER_PAGE = 10;
  INITIAL_DATE = '2015-01-01';

  state: TTradeHistory = {
    filteredTradesList: this.props.tradesList.slice(0, this.MAX_PER_PAGE),
    filteredTradesListAllPages: this.props.tradesList,
    currentPage: 1,
    totalCount: this.props.tradesList.length,
    maxPerPage: this.MAX_PER_PAGE,
    zoomValue: 'All',
    tradesFilter: this.props.tradesList.length > 0 ? {
      startDate: this.props.tradesList[this.props.tradesList.length - 1].date,
      endDate: moment().format('YYYY-MM-DD'),
    } : {
      startDate: this.INITIAL_DATE,
      endDate: this.INITIAL_DATE,
    },
  };

  setZoomValue = (value: string) => {
    if (value !== this.state.zoomValue) {
      const today = moment().format('YYYY-MM-DD');
      let newStartDate = undefined;

      switch (value) {
        case '7d':
          newStartDate = moment(today).subtract(7, 'days').format('YYYY-MM-DD');
          break;
        case '1m':
          newStartDate = moment(today).subtract(1, 'months').format('YYYY-MM-DD');
          break;
        case '3m':
          newStartDate = moment(today).subtract(3, 'months').format('YYYY-MM-DD');
          break;
        case '1y':
          newStartDate = moment(today).subtract(1, 'years').format('YYYY-MM-DD');
          break;
        case 'All':
          newStartDate = this.props.tradesList[this.props.tradesList.length - 1].date;
          break;
      }

      if (newStartDate) {
        this.filterData(1, newStartDate, today, value);
      }

    }
  }

  dateFilterUpdate = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    this.filterData(1, moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD'), null);
  }

  tradesData = () => {
    const { filteredTradesList, tradesFilter } = this.state;

    return (
      this.props.tradesList.length > 0 ?
        (
          <>
            <h2 className={style.headline}>Trading history</h2>
            <CsvGenerator
              items={this.state.filteredTradesListAllPages}
              endpointDetails={{}}
              baseEndpoint={false}
              noPagination={true}
              fileName="market-trading-history"
            />
            <div className={style.filterHolders}>
              <Zoom value={this.state.zoomValue} setZoomValue={this.setZoomValue}/>
              <DateRangePicker
                updateState={this.dateFilterUpdate}
                startDate={new Date(tradesFilter.startDate)}
                endDate={new Date(tradesFilter.endDate)}
                maxDate={new Date(moment().format())}
                minDate={new Date(moment().subtract(1, 'years').format())}
              />
            </div>
            <TradeHistoryChart
              list={this.getDataFromRange(this.props.tradesList)}
              lastFilledTrades={this.getLastFilledTrades(this.props.tradesList)}
              startDate={tradesFilter.startDate}
              endDate={tradesFilter.endDate}
            />
            {filteredTradesList.length === 0 ? (
              <h4 className={elements.noItems}>
                No results returned for your selection
              </h4>
            ) : (
              <>
                <div className={[elements.zebraTableContainer, style.zebraTableContainer].join(' ')}>
                  <div className={elements.zebraTableRow}>
                    {['Date', 'Price', 'Amount', 'Value'].map((label: string) => {
                      return (
                        <div
                          key={label}
                          className={
                            [elements.zebraTableItem, style.zebraTableItem, elements.zebraTableHeader]
                            .join(' ')
                          }
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                  {
                    filteredTradesList.map((trade: IFilteredTradesList) => {
                      return (
                        <div key={trade.id} className={elements.zebraTableRow}>
                          <div className={[elements.zebraTableItem, style.zebraTableItem].join(' ')}>
                            {formatDate(trade.date)}
                          </div>
                          <div className={[elements.zebraTableItem, style.zebraTableItem].join(' ')}>
                            <CurrencyElement value={trade.price_per_share} currency={'usd'}/>
                          </div>
                          <div className={[elements.zebraTableItem, style.zebraTableItem].join(' ')}>
                            {trade.number_of_shares}
                          </div>
                          <div className={[elements.zebraTableItem, style.zebraTableItem].join(' ')}>
                            <CurrencyElement value={trade.value} currency={'usd'}/>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
                <Pagination
                  currentPage={this.state.currentPage}
                  maxPerPage={this.state.maxPerPage}
                  totalCount={this.state.totalCount}
                  filterData={this.filterData}
                />
              </>
            )}
          </>
        ) : (
          <h4 className={elements.noItems}>
            There is no trade history yet. <br/>
            Be the first one to complete a trade on this market!
          </h4>
        )
    );
  }

  filterData = (
    newPage: number = this.state.currentPage,
    dateFrom: string = this.state.tradesFilter.startDate,
    dateTo: string = this.state.tradesFilter.endDate,
    zoomValue = this.state.zoomValue
  ) => {
    const newFilteredTradesList = this.getDataFromRange(this.props.tradesList, dateFrom, dateTo);

    this.setState({
      zoomValue,
      filteredTradesList: newFilteredTradesList.slice(
        Math.max(0, newPage - 1) * this.state.maxPerPage, newPage * this.state.maxPerPage
      ),
      filteredTradesListAllPages: newFilteredTradesList,
      currentPage: newPage,
      totalCount: newFilteredTradesList.length,
      tradesFilter: {
        startDate: dateFrom,
        endDate: dateTo,
      },
    });
  }

  getDataFromRange = (data: IFilteredTradesList[],
                      from: string = this.state.tradesFilter.startDate,
                      to: string = this.state.tradesFilter.endDate) => {
    return data.filter((el: IFilteredTradesList) => {
      return moment(el.date).isSameOrAfter(from) && moment(el.date).isSameOrBefore(to);
    });
  }

  getLastFilledTrades = (data: IFilteredTradesList[]) => {
    const lastFilledTrade = data.find((el: IFilteredTradesList) =>
      el.value !== 0 && moment(el.date).isBefore(this.state.tradesFilter.startDate));

    if (lastFilledTrade) {
      return data.filter((el: IFilteredTradesList) => el.date === lastFilledTrade.date);
    }

    return null;
  }

  render() {
    return (
      <div>
        {this.tradesData()}
      </div>
    );
  }
}
